import { useIntl } from 'react-intl';
import type {
    PatientDetailDto,
    TreatmentSiteDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import { postIntake } from '~/backend/connector-next-api/patientWritesClient';
import { enqueueSnackbar } from '~/utils/notifications';
import {
    intakeBody,
    storedIntake,
    suggestedSites,
    workflowNotStarted,
    type IntakeValues,
} from '../model';
import { usePatientWrite } from './usePatientWrite';
import {
    useTreatmentStartEdit,
    type TreatmentStartEdit,
} from './useTreatmentStartEdit';

/** A patient as far as editing its intake goes. */
type IntakePatient = Pick<
    PatientDetailDto,
    | 'id'
    | 'version'
    | 'courseStage'
    | 'siteId'
    | 'radiationOncologistId'
    | 'plannerId'
    | 'treatmentStart'
    | 'bodyParts'
>;

export type PatientIntake = {
    /** The site and people shown: each stored one, or else its suggestion. */
    shown: Omit<IntakeValues, 'treatmentStart'>;
    /** The offered sites the patient's images suggest, best first. */
    suggestedSites: readonly TreatmentSiteDto[];
    /** Whether the site shown is a suggestion nobody chose. */
    siteSuggested: boolean;
    /** Whether the planner shown is a suggestion nobody chose. */
    plannerSuggested: boolean;
    treatmentStart: TreatmentStartEdit;
    /** Saves `change` over the stored values; the suggestions stay unsaved. */
    choose: (change: Partial<IntakeValues>) => void;
    /** True while a write is in flight, or while none can be sent. */
    disabled: boolean;
};

/**
 * A patient's intake as a user edits it. While the workflow has not started,
 * an unchosen site is suggested from the images and an unchosen planner is
 * the user `meId`. The treatment start is never suggested. Each choice is
 * saved at once, on its own; a suggestion is never saved until the user
 * chooses it. A failed write is reported as not saved.
 */
export function usePatientIntake(
    patient: IntakePatient | undefined,
    sites: readonly TreatmentSiteDto[],
    meId: string | undefined
): PatientIntake {
    const intl = useIntl();
    const stored = storedIntake(patient ?? {});
    const notStarted = patient !== undefined && workflowNotStarted(patient);
    const { write, canWrite, isSaving } = usePatientWrite(
        { id: patient?.id, version: patient?.version },
        (values: IntakeValues, id, version) =>
            postIntake(id, intakeBody(values), version),
        () =>
            enqueueSnackbar(
                intl.formatMessage({ id: 'patientList.intake.saveFailed' }),
                { variant: 'error' }
            )
    );
    const choose = (change: Partial<IntakeValues>, onError?: () => void) =>
        write({ ...stored, ...change }, onError);

    const treatmentStart = useTreatmentStartEdit(
        stored.treatmentStart,
        (value, onError) => choose({ treatmentStart: value }, onError)
    );
    const suggested = notStarted
        ? suggestedSites(sites, patient.bodyParts)
        : [];
    const suggestedSiteId = suggested[0]?.id ?? undefined;
    const suggestedPlannerId = notStarted ? meId : undefined;
    const shown = {
        siteId: stored.siteId ?? suggestedSiteId,
        radiationOncologistId: stored.radiationOncologistId,
        plannerId: stored.plannerId ?? suggestedPlannerId,
    };

    return {
        shown,
        suggestedSites: suggested,
        siteSuggested:
            stored.siteId === undefined && suggestedSiteId !== undefined,
        plannerSuggested:
            stored.plannerId === undefined && suggestedPlannerId !== undefined,
        treatmentStart,
        choose,
        disabled: isSaving || !canWrite,
    };
}
